import React, { useState, useEffect } from 'react';
import { getCultures, deleteCulture, toggleCultureFeatured } from '../api/api';
import { Link } from 'react-router-dom';
import './AdminCultures.css';

const AdminCultures = () => {
  const [cultures, setCultures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchCultures();
    // eslint-disable-next-line
  }, [filter]);

  const fetchCultures = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === 'verified') params.verified = true;
      if (filter === 'unverified') params.verified = false;
      if (filter === 'featured') params.featured = true;

      const response = await getCultures(params);
      setCultures(response.data.cultures || response.data || []);
      setError('');
    } catch (err) {
      setError('Failed to fetch cultures');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, cultureName) => {
    if (!window.confirm(`Are you sure you want to delete ${cultureName}? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteCulture(id);
      alert('Culture deleted successfully');
      fetchCultures();
    } catch (err) {
      alert('Failed to delete culture');
      console.error(err);
    }
  };

  const handleToggleFeatured = async (id, currentStatus) => {
    try {
      await toggleCultureFeatured(id);
      alert(`Culture ${currentStatus ? 'unfeatured' : 'featured'} successfully`);
      fetchCultures();
    } catch (err) {
      alert('Failed to update featured status');
      console.error(err);
    }
  };

  if (loading) return <div className="loading">Loading cultures...</div>;

  return (
    <div className="admin-cultures">
      <div className="cultures-header">
        <h2>Manage Cultural Information</h2>
        <Link to="/admin/dashboard/cultures/new" className="btn btn-primary">
          Add New Culture
        </Link>
      </div>

      <div className="filters">
        <button
          className={filter === 'all' ? 'active' : ''}
          onClick={() => setFilter('all')}
        >
          All
        </button>
        <button
          className={filter === 'verified' ? 'active' : ''}
          onClick={() => setFilter('verified')}
        >
          Verified
        </button>
        <button
          className={filter === 'unverified' ? 'active' : ''}
          onClick={() => setFilter('unverified')}
        >
          Unverified
        </button>
        <button
          className={filter === 'featured' ? 'active' : ''}
          onClick={() => setFilter('featured')}
        >
          Featured
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {cultures.length === 0 ? (
        <div className="empty-state">
          <p>No cultural information found. Start by adding your first culture!</p>
        </div>
      ) : (
        <div className="cultures-table">
          <table>
            <thead>
              <tr>
                <th>Culture Name</th>
                <th>Display Name</th>
                <th>Language</th>
                <th>Status</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cultures.map((culture) => (
                <tr key={culture._id}>
                  <td>
                    <strong>{culture.cultureName}</strong>
                  </td>
                  <td>{culture.displayName}</td>
                  <td>{culture.language?.name || 'N/A'}</td>
                  <td>
                    <div className="status-badges">
                      {culture.verified && (
                        <span className="badge verified">Verified</span>
                      )}
                      {culture.featured && (
                        <span className="badge featured">Featured</span>
                      )}
                      {!culture.verified && (
                        <span className="badge unverified">Unverified</span>
                      )}
                    </div>
                  </td>
                  <td>
                    {new Date(culture.updatedAt).toLocaleDateString()}
                  </td>
                  <td className="actions">
                    <Link
                      to={`/cultures/${culture.cultureName}`}
                      className="btn-view"
                      target="_blank"
                    >
                      View
                    </Link>
                    <Link
                      to={`/admin/dashboard/cultures/edit/${culture._id}`}
                      className="btn-edit"
                    >
                      Edit
                    </Link>
                    <Link
                      to={`/admin/dashboard/cultures/media/${culture._id}`}
                      className="btn-media"
                    >
                      Gallery
                    </Link>
                    <button
                      onClick={() => handleToggleFeatured(culture._id, culture.featured)}
                      className="btn-feature"
                    >
                      {culture.featured ? 'Unfeature' : 'Feature'}
                    </button>
                    <button
                      onClick={() => handleDelete(culture._id, culture.displayName)}
                      className="btn-delete"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminCultures;
